"use client";

import Script from "next/script";
import { type RefObject, useEffect, useRef, useState } from "react";

import { useUsercentricsConsent } from "@/hooks/use-usercentrics-consent";
import { Divider, Notification, TextD2C } from "@/scott/ui/elements";

declare global {
  interface Window {
    universal_variable?: UniversalVariable;
    // biome-ignore lint/suspicious/noExplicitAny: object is provided by Fitanalytics
    _fitAnalytics?: any;
  }
}

const USERCENTRICS_FITANALYTICS_SERVICE_NAME = "Fitanalytics";
const USERCENTRICS_CATEGORY = "functional";

interface UniversalVariable {
  page: {
    country: string;
    language: string;
    brand: string;
    division: string;
    type: string;
    page_id: string;
    currency: string;
    product: {
      product_name: string;
      product_id: string;
      product_price: string;
      product_type?: string;
      category_name?: string;
    };
  };
  user: {
    id: string;
    login_status: boolean;
    login_method: string;
    ltv_amount: number;
    ltv_orders: number;
  };
}

export interface ProductSizeFinderFitanalyticsProps {
  microcopies: {
    title: string;
    description: string;
    consentMessage: string;
    consentActionLabel: string;
  };
  productData: {
    productCode: string;
    baseProductCode: string;
    name: string;
    priceValue: number;
    category?: string;
  };
  language: string;
  country: string;
}

export function ProductSizeFinderFitanalytics(
  props: Readonly<ProductSizeFinderFitanalyticsProps>
) {
  const fitanalyticsConsented = useUsercentricsConsent(
    USERCENTRICS_FITANALYTICS_SERVICE_NAME,
    USERCENTRICS_CATEGORY
  );

  const { microcopies, productData } = props;
  const [dataReady, setDataReady] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetVisible = useFitanalyticsButtonVisible(
    widgetRef,
    fitanalyticsConsented === true
  );

  useEffect(() => {
    if (fitanalyticsConsented !== true) {
      return;
    }

    window.universal_variable = {
      page: {
        country: props.country,
        language: props.language,
        brand: "Scott",
        division: "Outdoor",
        type: "Product Details Page",
        page_id: "PDP",
        currency: "EUR",
        product: {
          product_name: productData.name,
          product_id: productData.baseProductCode,
          product_price: productData.priceValue.toFixed(2),
          category_name: productData.category,
        },
      },
      user: {
        id: "",
        login_status: false,
        login_method: "",
        ltv_amount: 0,
        ltv_orders: 0,
      },
    };

    setDataReady(true);
  }, [fitanalyticsConsented, productData, props.language, props.country]);

  // Usercentrics has not answered yet, or never loaded (e.g. blocked).
  // Without a consent answer we can neither load the widget nor ask for consent.
  if (fitanalyticsConsented === null) {
    return null;
  }

  const intro = (
    <>
      <TextD2C style="bold" variant="p-small">
        {microcopies.title}
      </TextD2C>
      <TextD2C variant="p-small">{microcopies.description}</TextD2C>
    </>
  );

  if (fitanalyticsConsented === false) {
    return (
      <>
        <Divider className="my-6" />

        <div className="space-y-3">
          {intro}

          <Notification
            action={{
              label: microcopies.consentActionLabel,
              onClick: () => window.UC_UI?.showSecondLayer(),
            }}
            backgroundColor="alabastergrey"
            className="mt-4"
            message={microcopies.consentMessage}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {dataReady && (
        <>
          <link
            data-uclw-version="V3"
            href="https://widget.fitanalytics.com/css/widget_v3.css?rel=54.28.3"
            rel="stylesheet"
            type="text/css"
          />

          <link
            data-uclw-version="V3"
            href="https://customizations.fitanalytics.com/css/widget_scottsports_v3.css?rel=54.28.3"
            rel="stylesheet"
            type="text/css"
          />

          <Script
            src="https://integrations.fitanalytics.com/shop/scottsports/pdp.js"
            strategy="afterInteractive"
          />
        </>
      )}

      {/* Hidden until Fit Analytics shows its button: it supports only the products in its own catalogue. */}
      <div className={widgetVisible ? undefined : "hidden"}>
        <Divider className="my-6" />
        <div className="space-y-3">{intro}</div>
      </div>

      {/* Fit Analytics renders its button into this div, so it must stay in the layout. It has no height while the button is hidden. */}
      <div
        className="product_detail__description__size product_detail__fitanalytics_widget m-0"
        data-testid="fitanalytics-widget"
        ref={widgetRef}
      />
    </>
  );
}

const FITANALYTICS_BUTTON_SELECTOR = ".fitanalytics__button";

/**
 * Fit Analytics always injects its button, but keeps it at `display: none`
 * for products it does not know. It may show or hide it later, so keep watching.
 */
function useFitanalyticsButtonVisible(
  widgetRef: RefObject<HTMLDivElement | null>,
  enabled: boolean
): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!(enabled && widget)) {
      setVisible(false);
      return;
    }

    const update = () => {
      const button = widget.querySelector<HTMLElement>(
        FITANALYTICS_BUTTON_SELECTOR
      );
      setVisible(Boolean(button) && button?.style.display !== "none");
    };

    update();

    const observer = new MutationObserver(update);
    observer.observe(widget, {
      attributes: true,
      attributeFilter: ["style", "class"],
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [widgetRef, enabled]);

  return visible;
}
